<template>
  <Transition name="modal">
    <!--
      A native <dialog> (opened via showModal) rather than a plain fixed div:
      dialogs render in the browser's top layer, which always paints above
      regular content regardless of z-index. That's required so this modal
      still shows up correctly when nested inside another top-layer modal
      (e.g. RecordModal, which is itself a <dialog>) — a plain div can never
      out-z-index a top-layer ancestor.
    -->
    <dialog
      v-if="show"
      ref="dialogRef"
      class="modal-mask"
      @vue:mounted="dialogRef?.showModal()"
      @cancel.prevent="emit('close')"
      @click="clickOutside"
    >
      <div class="modal-container">
        <div v-if="hasControls" class="modal-controls"></div>
        <div class="modal-content r-6" :class="{ flush }">
          <slot></slot>
        </div>
        <div v-if="hasControls" class="modal-controls">
          <slot name="controls"></slot>
        </div>
      </div>
    </dialog>
  </Transition>
</template>

<script setup lang="ts">
import { computed, ref, useSlots, watch } from "vue"

interface Props {
  show: boolean
  clickOutsideToClose?: boolean
  flush?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  clickOutsideToClose: true,
  flush: false,
})

const emit = defineEmits<{
  close: []
}>()

const slots = useSlots()
const dialogRef = ref<HTMLDialogElement | null>(null)

// Native <dialog> close is only automatic for the browser's OWN default
// actions (Escape, or its own backdrop-click handling) — those fire the
// dialog's native `cancel`/`close` events independent of Vue entirely.
// A caller setting `show` to false via ordinary state (e.g. a Cancel button's
// click handler) has no such native trigger, so nothing ever calls the
// dialog's own `.close()` — v-if removing it from the Vue tree does NOT
// close it first, leaving it open (and in the top layer) for however long
// the leave transition takes, or indefinitely if that transition never
// resolves. Watch `show` and drive `.close()` imperatively so every path
// that flips `show` false actually closes the dialog, not just the ones
// that happen to go through the browser's own default action.
watch(
  () => props.show,
  (show) => {
    if (!show) dialogRef.value?.close()
  }
)

// Escape fires the dialog's native `cancel`, which would close it on its own —
// `.prevent` above keeps closing controlled by `show`, so a parent can veto it
// (e.g. to confirm discarding unsaved edits) without the dialog vanishing.

const hasControls = computed(() => !!slots.controls)

function clickOutside(event: MouseEvent) {
  // A click lands on the <dialog> itself (rather than a descendant) both for
  // true backdrop clicks and for clicks in the dialog's own unfilled box —
  // .modal-mask has no padding of its own, so this only fires for the former.
  if (props.clickOutsideToClose && event.target === dialogRef.value) {
    emit("close")
  }
}
</script>

<style>
.modal-mask {
  /* Reset <dialog> UA defaults; positioning/centering comes from the UA's
     built-in showModal() behavior (position: fixed, inset: 0, margin: auto). */
  border: none;
  padding: 0;
  max-width: 100%;
  max-height: 100%;
  background: transparent;
  color: inherit;

  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.modal-mask::backdrop {
  /* Make the background slightly transparent based on --auger-dark */
  background-color: color-mix(in srgb, var(--auger-dark) 90%, transparent);
}

.modal-controls {
  margin: 0 1.5rem;
  width: 3rem;
  align-self: flex-start;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;

  > * {
    box-shadow: var(--auger-shadow-overlay);
  }
}

.modal-container {
  max-width: 100%;
  /* max-height: calc( 100% - 4rem ) */
  transition: all 0.2s ease;
  overflow: hidden;
  display: flex;

  .modal-content {
    background: var(--auger-bg);

    /* overflow: auto */
    max-height: 90vh;
    overflow: scroll;
    max-width: 100%;
    padding: 1.4rem 1.5rem;
    box-shadow: var(--auger-shadow-overlay);
    flex-grow: 1;
    z-index: 9999;

    &.flush {
      padding: 0;
      overflow: hidden;
      display: flex;
    }
  }
}

.modal-card {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;

  .modal-card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    background: var(--auger-bg-muted);
    border-bottom: 1px solid var(--auger-border);
    flex-shrink: 0;
  }

  .modal-card-title {
    font-size: var(--auger-size-4);
    font-weight: 600;
    color: var(--auger-text-strong);
    flex-grow: 1;
    margin: 0;
  }

  .modal-card-body {
    padding: 1.5rem;
    overflow-y: auto;
    flex: 1;
  }

  .modal-card-foot {
    padding: 1rem 1.5rem;
    background: var(--auger-bg-muted);
    border-top: 1px solid var(--auger-border);
    flex-shrink: 0;
  }
}

/* For mobile devices when the modal is shown, take up almost the entire screen */
/* Push the controls to the bottom and make them horizontal */
@media screen and (max-width: 768px) {
  .modal-container {
    flex-direction: column;
    height: 95%;
    width: 95%;

    /* Hide the first modal-controls */
    > .modal-controls:first-child {
      display: none;
    }

    .modal-controls {
      margin: 0;
      padding-top: 1rem;
      height: 4rem;
      width: 100%;
      flex-direction: row;

      button.btn {
        min-width: 3rem;
      }
    }

    .modal-content {
      overflow: auto;
      margin: 0;
    }
  }
}

/* The following styles are auto-applied to elements with transition="modal" when
 * their visibility is toggled by Vue.js. */

.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.8);
}
</style>
